import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {PorQue} from '@/components/ui/por-que'
import {Tag} from '@/components/ui/tag'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {useDeclararSinEventos, useRevocarSinEventos, type SgsstItem} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'

/** The declaration (author, date, revocable) is the soporte; the backend refuses it when Kairos already
 * knows of events in the year. */
export function SinEventosBlock({
  businessId,
  item,
  year,
  editable,
}: {
  businessId: string
  item: SgsstItem
  year: number
  editable: boolean
}) {
  const {t} = useTranslation()
  const declarar = useDeclararSinEventos(businessId)
  const revocar = useRevocarSinEventos(businessId)
  const [confirm, setConfirm] = useState<'declarar' | 'revocar' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [nodo, setNodo] = useState<HTMLElement | null>(null)
  const declaracion = item.sin_eventos
  const pending = declarar.isPending || revocar.isPending

  function run(action: 'declarar' | 'revocar') {
    const handlers = {
      onSuccess: () => {
        setConfirm(null)
        setError(null)
      },
      onError: (err: unknown) => setError(translateApiError(err)),
    }
    if (action === 'declarar') declarar.mutate({code: item.code}, handlers)
    else if (declaracion) revocar.mutate({code: item.code, declaracionId: declaracion.id}, handlers)
  }

  return (
    <div ref={setNodo} className="flex flex-wrap items-center gap-2" data-testid={`sin-eventos-${item.code}`}>
      {declaracion ? (
        <>
          <Tag tone="green" data-testid={`sin-eventos-chip-${item.code}`}>
            {t('evidence.sinEventos.chip', {year})}
          </Tag>
          <PorQue
            label={t('evidence.sinEventos.porqueLabel')}
            testId={`sin-eventos-porque-${item.code}`}
            contenedor={nodo}>
            {t('evidence.sinEventos.porque', {
              nombre: declaracion.declarado_por_nombre,
              fecha: formatDateOnly(declaracion.declarado_en.slice(0, 10)),
            })}
            {declaracion.texto ? ` «${declaracion.texto}»` : ''}
          </PorQue>
          {editable && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setConfirm('revocar')}
              data-testid={`sin-eventos-revocar-${item.code}`}>
              {t('evidence.sinEventos.revocar')}
            </Button>
          )}
        </>
      ) : (
        editable && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setConfirm('declarar')}
            data-testid={`sin-eventos-declarar-${item.code}`}>
            {t('evidence.sinEventos.declarar', {year})}
          </Button>
        )
      )}
      {error && (
        <p className="text-xs text-destructive" role="alert" data-testid={`sin-eventos-error-${item.code}`}>
          {error}
        </p>
      )}
      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(open) => {
          if (!open) setConfirm(null)
        }}
        title={
          confirm === 'revocar' ? t('evidence.sinEventos.revocarTitle') : t('evidence.sinEventos.declararTitle', {year})
        }
        description={
          confirm === 'revocar' ? t('evidence.sinEventos.revocarBody') : t('evidence.sinEventos.declararBody')
        }
        confirmLabel={confirm === 'revocar' ? t('evidence.sinEventos.revocar') : t('evidence.sinEventos.confirmar')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => run(confirm ?? 'declarar')}
        pending={pending}
      />
    </div>
  )
}
